import { axiosInstanceV2, axiosInstance } from "../axios";
import { IUser, IUserRegistrationData } from "../types";
import { snakeCaseDictToCamelCaseDict, cleanCPFCNPJ } from "../utils";

export interface GetTokenResponse {
  token: string;
  tokenExpirationDate?: string;
  user: IUser;
}

export async function getToken({
  username,
  password,
  agreement_id,
  integratedLogin,
  idp,
}: {
  username: string;
  password?: string;
  integratedLogin?: string;
  idp?: string;
  agreement_id?: number | string;
}): Promise<GetTokenResponse> {
  const loginEndpoints: Record<string, string> = {
    neo: '/integrations/neo/login/',
    voalle: '/voalle/login/',
    surf: '/surf/login/',
    medlar: '/medlar/login/',
    mk: '/mk/login/',
    maxx: '/maxx/login/',
    youcast: '/youcast/login/',
    isuper: '/isuper/login/',
    adapter: '/adapter/login/',
    leste: '/leste/login/',
    sercomtel: '/sercomtel/slogin/',
  };

  if (integratedLogin && loginEndpoints[integratedLogin]) {
    const payload =
      integratedLogin === 'surf' || integratedLogin === 'medlar'
        ? { username: username }
        : integratedLogin === 'sercomtel'
          ? { document: username, password }
          : { username, password, agreement_id, idp: idp || integratedLogin };

    if (integratedLogin === 'neo') {
      return axiosInstanceV2.post(loginEndpoints[integratedLogin], payload).then(({ data: { token, ...data } }) => ({
        token,
        tokenExpirationDate: data.token_expiration_date,
        user: snakeCaseDictToCamelCaseDict(data) as IUser,
      }));
    }

    return axiosInstance.post(loginEndpoints[integratedLogin], payload).then(({ data: { token, ...data } }) => ({
      token,
      tokenExpirationDate: data.token_expiration_date,
      user: snakeCaseDictToCamelCaseDict(data) as IUser,
    }));
  }

  return axiosInstance
    .post('/users/tokens/' + (agreement_id ? `?agreement_id=${agreement_id}` : ''), {
      username,
      password,
      agreement_id,
    })
    .then(({ data }) => ({
      token: data.token,
      user: snakeCaseDictToCamelCaseDict(data.user) as IUser,
    }));
}

export async function getExClientLogin({ cpf }: { cpf: string }): Promise<GetTokenResponse> {
  return axiosInstanceV2.post('/agreement/ex_client/', { cpf }).then(({ data }) => ({
    token: data.token,
    user: snakeCaseDictToCamelCaseDict(data.user) as IUser,
  }));
}

export async function forgotPass({ email }: { email: string }) {
  return axiosInstance.post('/users/reset-password/', { email });
}

export function registerUser(user: IUserRegistrationData, agreementId: number): Promise<IUser & { token: string }> {
  return axiosInstance
    .post(`/profile/?agreement_id=${agreementId}`, { ...user, cpf: cleanCPFCNPJ(user.cpf) })
    .then(({ data }) => snakeCaseDictToCamelCaseDict(data) as IUser & { token: string });
}

export async function validateCPF(cnpjCpf: string, agreementId: number) {
  return axiosInstanceV2
    .post(`/agreement/validate/?agreement_id=${agreementId}`, {
      cpf: cleanCPFCNPJ(cnpjCpf),
    })
    .then(({ data }) => data);
}

export async function vinculateCPF(cnpjCpf: string, agreementId: number) {
  return axiosInstanceV2
    .post(`/agreement/vinculate/?agreement_id=${agreementId}`, {
      cpf: cleanCPFCNPJ(cnpjCpf),
    })
    .then(({ data }) => data);
}

export function validatePromocode(code: string) {
  return axiosInstance.post('/validate_promocode/', { promocode: code });
}

export function vinculateExternalCode(code: string, agreementId: number) {
  return axiosInstanceV2
    .post(`/agreement/vinculate/?agreement_id=${agreementId}`, {
      external_code: masks.unmask(code),
    })
    .then(({ data }) => data);
}
